import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { api } from '../api.ts';
import { Notice } from '../components/Notice.tsx';
import { copy } from '../copy/en-CA.ts';
import { describeResetFailure } from '../describe.ts';
import { useSession } from '../session.tsx';

const c = copy.resetPassword;

type Phase =
  | { kind: 'checking' }
  | { kind: 'ready'; email: string }
  /** The link cannot be used; `askAgain` offers a new one (not for a deactivated account). */
  | { kind: 'unusable'; message: string; askAgain: boolean };

/**
 * Where the emailed reset link lands (owner, 4 November 2026). Reachable
 * signed in or not: a person may open the link on a phone that is still
 * signed in. The link's code is read once and taken out of the address bar,
 * so it is not left in the browser's history. The page checks the link
 * first, so a person whose link has run out is told before typing anything.
 * Saving signs the account out everywhere, this browser included, and the
 * sign-in page follows with the email filled in.
 */
export function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useSession();
  const [token] = useState(() => new URLSearchParams(location.search).get('token') ?? '');
  const [phase, setPhase] = useState<Phase>(() => (token ? { kind: 'checking' } : { kind: 'unusable', message: c.noLink, askAgain: true }));
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (location.search) void navigate('/reset-password', { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    if (!token) return;
    let live = true;
    api
      .checkResetLink(token)
      .then(({ email }) => {
        if (live) setPhase({ kind: 'ready', email });
      })
      .catch((err: unknown) => {
        if (!live) return;
        const d = describeResetFailure(err);
        if (d.unusableLink || d.accountInactive) setPhase({ kind: 'unusable', message: d.message, askAgain: d.unusableLink });
        // The server could not be reached or failed: say so, and let the person try the form anyway.
        else {
          setPhase({ kind: 'ready', email: '' });
          setError(d.message);
        }
      });
    return () => {
      live = false;
    };
  }, [token]);

  // When the form gives way to "this link cannot be used", move focus to the heading, so it is read out.
  useEffect(() => {
    if (phase.kind === 'unusable') heading.current?.focus();
  }, [phase.kind]);

  const submit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError(c.mismatch);
      return;
    }
    setWorking(true);
    try {
      const { email } = await api.completePasswordReset(token, next);
      // Every session of the account has ended, this browser's included.
      setUser(null);
      void navigate('/', { replace: true, state: { passwordReset: true, email } });
    } catch (err) {
      const d = describeResetFailure(err);
      if (d.unusableLink || d.accountInactive) setPhase({ kind: 'unusable', message: d.message, askAgain: d.unusableLink });
      else setError(d.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="signin-page">
      <div className="wordmark">
        <span className="wordmark-name">{copy.appName}</span>
        <span className="wordmark-sub">{copy.appSub}</span>
      </div>
      {phase.kind === 'checking' ? (
        <div className="card signin-card">
          <p role="status">{c.checking}</p>
        </div>
      ) : null}
      {phase.kind === 'unusable' ? (
        <div className="card signin-card">
          <div className="page-title">
            <h1 ref={heading} tabIndex={-1}>
              {c.title}
            </h1>
          </div>
          <Notice kind="error">{phase.message}</Notice>
          <div className="row">
            {phase.askAgain ? (
              <Link className="btn primary" to="/forgot-password">
                {c.askAgain}
              </Link>
            ) : null}
            <Link className="btn" to="/">
              {c.back}
            </Link>
          </div>
        </div>
      ) : null}
      {phase.kind === 'ready' ? (
        <form
          className="card signin-card"
          onSubmit={(e) => {
            void submit(e);
          }}
        >
          <div className="page-title">
            <h1>{c.title}</h1>
            <p>{c.intro}</p>
          </div>
          {phase.email ? (
            <div className="field">
              <label htmlFor="reset-email">{c.email}</label>
              {/* Read-only, and there so the browser's password manager saves the new password for the right account. */}
              <input id="reset-email" className="input" type="email" autoComplete="username" readOnly value={phase.email} />
            </div>
          ) : null}
          <div className="field">
            <label htmlFor="next">{c.next}</label>
            <input
              id="next"
              className="input"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={next}
              onChange={(e) => {
                setNext(e.target.value);
              }}
            />
          </div>
          <div className="field">
            <label htmlFor="confirm">{c.confirm}</label>
            <input
              id="confirm"
              className="input"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
              }}
            />
          </div>
          {error ? <Notice kind="error">{error}</Notice> : null}
          <div className="row">
            <button type="submit" className="btn primary" disabled={working}>
              {working ? c.working : c.submit}
            </button>
            <Link className="btn" to="/">
              {c.back}
            </Link>
          </div>
        </form>
      ) : null}
    </div>
  );
}
